import { ChevronRightIcon, CreditCardIcon, MegaphoneIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import { useCreateCampaign } from '../hooks/useCreateCampaign';

interface CampaignTypeCardProps {
  illustration: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  illustrationBg?: string;
}

const CampaignTypeCard = ({
  illustration,
  title,
  description,
  onClick,
  illustrationBg = 'bg-bg-muted-light',
}: CampaignTypeCardProps) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className="group w-[296px] cursor-pointer gap-5 rounded-md border border-border bg-background px-5 pt-5 pb-6 text-foreground shadow-none transition-all duration-200 hover:scale-[1.02] hover:shadow-md focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
    >
      <div
        className={cn(
          'relative flex h-40 items-center justify-center overflow-hidden rounded-lg',
          illustrationBg
        )}
      >
        {illustration}
      </div>
      <CardContent className="flex flex-1 flex-col gap-8 p-0">
        <div className="flex flex-1 flex-col gap-2">
          <CardTitle className="px-0.5 text-xl font-medium tracking-tight">{title}</CardTitle>
          <CardDescription className="px-1 leading-5">{description}</CardDescription>
        </div>
        <div className="flex items-center gap-1 text-sm font-medium text-primary group-hover:underline">
          {t('common.create')}
          <ChevronRightIcon size={16} className="text-slate-500" />
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Shared "stage" for the campaign illustrations: a soft blue radial glow, a set of
 * concentric white halo rings, the centered icon shape, and a few decorative dots —
 * mirroring the Figma empty-state design.
 */
const IllustrationStage = ({ children }: { children: React.ReactNode }) => (
  <div className="relative flex size-full items-center justify-center">
    {/* Soft radial glow */}
    <div className="absolute size-40 rounded-full bg-[#4691FF]/40 blur-2xl" />
    {/* Concentric halo rings */}
    <div className="absolute size-28 rounded-full bg-background/40" />
    <div className="absolute size-24 rounded-full bg-background/70" />
    {children}
    {/* Decorative dots */}
    <span className="absolute top-[40%] left-[20%] size-1.5 rounded-full bg-blue-700" />
    <span className="absolute top-[26%] right-[22%] size-2 rounded-full bg-blue-700/80" />
    <span className="absolute right-[26%] bottom-[26%] size-2.5 rounded-full bg-blue-200/80" />
    <span className="absolute bottom-[22%] left-[30%] size-1 rounded-full bg-slate-300" />
  </div>
);

/** Outreach illustration: megaphone in a white circle */
const OutreachIllustration = () => (
  <IllustrationStage>
    <div className="relative flex size-16 items-center justify-center rounded-full bg-background shadow-sm">
      <MegaphoneIcon className="size-6 text-accent" strokeWidth={1.5} />
    </div>
  </IllustrationStage>
);

/** Payment recovery illustration: card inside a white shield */
const PaymentRecoveryIllustration = () => (
  <IllustrationStage>
    <div className="relative flex size-20 items-center justify-center">
      <svg
        viewBox="0 0 84 84"
        fill="none"
        className="absolute size-20 drop-shadow-sm"
        aria-hidden="true"
      >
        <path
          d="M42 6 12 17v22c0 17.7 15.5 28.4 30 34 14.5-5.6 30-16.3 30-34V17L42 6Z"
          className="fill-background"
        />
      </svg>
      <CreditCardIcon className="relative size-6 text-accent" strokeWidth={1.5} />
    </div>
  </IllustrationStage>
);

export const EmptyCampaignsPage = () => {
  const { t } = useTranslation();
  const { createCampaign } = useCreateCampaign();

  return (
    <div className="flex flex-col items-center gap-6 pt-12 pb-6">
      <h3 className="text-2xl font-semibold tracking-tight">{t('campaigns.chooseCampaignType')}</h3>

      <div className="flex flex-wrap justify-center gap-4">
        <CampaignTypeCard
          illustration={<OutreachIllustration />}
          title={t('campaigns.outreach')}
          description={t('campaigns.outreachCardDescription')}
          onClick={() => void createCampaign('marketing')}
        />

        <CampaignTypeCard
          illustration={<PaymentRecoveryIllustration />}
          title={t('campaigns.paymentRecovery')}
          description={t('campaigns.paymentRecoveryCardDescription')}
          onClick={() => void createCampaign('payment_recovery')}
        />
      </div>
    </div>
  );
};
